/**
 * CockpitHero.jsx — Diplomexa Institutional Luxury cockpit hero.
 *
 * The shared visual language behind every "billion-dollar feel" page
 * header.  Replaces the same ~30 lines of foil-stripe + bg-noise +
 * eyebrow + Cormorant + italic-gold accent + divider-gold-hairline +
 * description pattern across ~12 surfaces.
 *
 * Usage:
 *
 *   <CockpitHero
 *     eyebrow="Delegate Portal · Personal briefing"
 *     eyebrowIcon={Sparkles}
 *     title="Hello,"
 *     titleAccent="Aarav"            // Rendered in italic Cormorant gold
 *     titleTail="."                   // Optional trailing fragment (defaults "")
 *     description="Your conferences, courses, papers and certificates — assembled in one editorial briefing."
 *     primaryCta={{ label: "Manage events", to: "/dashboard/events", icon: ArrowUpRight, testid: "manage-events-button" }}
 *     secondaryCta={{ label: "View organisations", to: "/dashboard/organizations", testid: "manage-orgs-button" }}
 *     meta={[ ["DESK", "INDIA"], ["COMMITTEE", "UNSC"] ]}   // Optional Bloomberg-style meta strip
 *     testId="delegate-overview-hero"
 *     tone="default"                 // "default" | "tight" (less padding) | "wide"
 *   />
 *
 * Slots:
 *   children   — render extra content INSIDE the hero (e.g. stats grid)
 *   rightSlot  — render a glassmorphic card / floating element to the right
 */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

// Pre-baked variants so every CockpitHero gets the same delightful
// staggered entrance without callers having to think about it.
const HERO_PARENT = {
  initial: {},
  animate: { transition: { staggerChildren: 0.07, delayChildren: 0.04 } },
};
const HERO_CHILD = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 220, damping: 24 } },
};


export default function CockpitHero({
  eyebrow,
  eyebrowIcon: EyebrowIcon,
  title,
  titleAccent,
  titleTail = "",
  description,
  primaryCta,
  secondaryCta,
  meta,
  rightSlot,
  testId = "cockpit-hero",
  tone = "default",
  className = "",
  children,
}) {
  const padding = tone === "tight"
    ? "p-6 sm:p-7"
    : tone === "wide"
    ? "p-8 sm:p-10 lg:p-12"
    : "p-7 sm:p-8";

  return (
    <motion.section
      data-testid={testId}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      className={`relative overflow-hidden rounded-sm border border-brand-gold/20 bg-brand-navy-dark text-brand-champagne shadow-editorial ${padding} ${className}`}
    >
      {/* Layered atmosphere ─ foil stripe, parchment noise, gold halo */}
      <div aria-hidden className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
      <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
      {/* Gold halo — gentle floaty drift instead of static blur */}
      <motion.div
        aria-hidden
        className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-gold/12 blur-3xl"
        animate={{ y: [0, 8, 0], x: [0, -6, 0], opacity: [0.85, 1, 0.85] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        variants={HERO_PARENT}
        initial="initial"
        animate="animate"
        className={`relative grid gap-8 ${rightSlot ? "lg:grid-cols-12 items-center" : ""}`}
      >
        <div className={rightSlot ? "lg:col-span-7" : ""}>
          {eyebrow ? (
            <motion.p variants={HERO_CHILD} className="eyebrow text-brand-gold flex items-center gap-2">
              {EyebrowIcon ? <EyebrowIcon className="h-3 w-3" /> : null}
              {eyebrow}
            </motion.p>
          ) : null}

          {title || titleAccent ? (
            <motion.h1
              variants={HERO_CHILD}
              className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-brand-champagne leading-tight"
            >
              {title}
              {title && titleAccent ? " " : null}
              {titleAccent ? (
                <span className="italic font-display font-medium text-brand-gold">
                  {titleAccent}
                </span>
              ) : null}
              {titleTail}
            </motion.h1>
          ) : null}

          <motion.div variants={HERO_CHILD} className="divider-gold-hairline mt-6 max-w-md" />

          {description ? (
            <motion.p variants={HERO_CHILD} className="mt-6 text-sm text-brand-champagne/70 max-w-2xl leading-relaxed">
              {description}
            </motion.p>
          ) : null}

          {meta && meta.length > 0 ? (
            <motion.div variants={HERO_CHILD} className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] font-mono uppercase tracking-[0.22em] text-brand-champagne/55">
              {meta.map(([k, v], i) => (
                <span key={`${k}-${i}`}>
                  <span className="text-brand-gold">{k} ·</span> {v}
                </span>
              ))}
            </motion.div>
          ) : null}

          {(primaryCta || secondaryCta) ? (
            <motion.div variants={HERO_CHILD} className="mt-7 flex flex-wrap items-center gap-3">
              {primaryCta ? <CtaButton cta={primaryCta} variant="primary" /> : null}
              {secondaryCta ? <CtaButton cta={secondaryCta} variant="outline" /> : null}
            </motion.div>
          ) : null}

          {children}
        </div>

        {rightSlot ? (
          <motion.div variants={HERO_CHILD} className="lg:col-span-5 hidden lg:block">{rightSlot}</motion.div>
        ) : null}
      </motion.div>
    </motion.section>
  );
}


function CtaButton({ cta, variant }) {
  const Icon = cta.icon;
  const cls =
    variant === "primary"
      ? "bg-brand-gold text-brand-navy-dark hover:bg-brand-gold-light shadow-gold-soft hover:shadow-gold-strong"
      : "border border-brand-gold/40 text-brand-champagne hover:bg-brand-gold/10 hover:text-brand-champagne hover:border-brand-gold bg-transparent";
  // motion-friendly press feedback; transform-only to avoid layout thrash.
  const className = `cta-pulse inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-xs uppercase tracking-[0.18em] font-bold transition-all duration-300 ${cls}`;
  const inner = (
    <>
      {cta.label}
      {Icon ? <Icon className="h-3.5 w-3.5" /> : <ArrowRight className="h-3.5 w-3.5" />}
    </>
  );
  if (cta.to) {
    return (
      <motion.span
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: "spring", stiffness: 380, damping: 22 }}
        className="inline-block"
      >
        <Link to={cta.to} data-testid={cta.testid} className={className}>
          {inner}
        </Link>
      </motion.span>
    );
  }
  return (
    <motion.button
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 380, damping: 22 }}
      type="button"
      onClick={cta.onClick}
      data-testid={cta.testid}
      className={className}
    >
      {inner}
    </motion.button>
  );
}
